import { useEffect, useState } from "react";
import {
    isChrome,
    isChromium,
    isEdgeChromium,
    isElectron,
    isFirefox,
    isMobile,
    isSafari,
    isTablet,
} from "react-device-detect";
import { createPortal } from "react-dom";
import { useHistory, useParams } from "react-router-dom";
import { UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { SpaceCategory } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { AppMode, FocusModeSidebarContentType } from "@lifeat/shared/system/AppModeTypes";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { getUserSubscriptionTier } from "@lifeat/shared/user/UserUtil";
import { useGlobalAudioContext } from "@contexts/GlobalAudioContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { VirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import { addOnCloseMainWindowEventListener, addOnShowMainWindowEventListener } from "@desktop/ToDestkopEventListeners";
import useOnUserDataLoaded from "@hooks/useOnUserDataLoaded";
import { updateLastViewedFocusSidebarContent } from "@user/service/UserPreferencesService";
import { initUserDisplayNameInDbIfMissing } from "@user/service/UserService";
import { RouteUtil } from "@util/index";
import AppMenuShortcutListeners from "../../features/desktop-app/AppMenuShortcutListeners";
import DesktopAppMainLanding from "../../features/desktop-app/views/MainWindow/DesktopAppMainLanding";
import ExploreView from "../../features/explore/ExploreView";
import { OnboardingVisibilityController } from "../../features/onboarding/OnboardingVisibilityController";
import useProjectsData from "../../features/projects/useProjectsData";
import { RoomContextProvider } from "../../features/room/RoomContext";
import { RoomControllerProvider } from "../../features/room/RoomController";
import { useRoomSpaceSync } from "../../features/room/hooks/useRoomSpaceSync";
import RoomChatSidebar from "../../features/room/room-chat/components/RoomChatSidebar";
import Planner from "../../features/task-management/planner/components/Planner";
import { useTaskMigration } from "../../features/task-management/useTaskMigration";
import { isLegacyUserWithFreeSpaceAccess } from "../../features/virtual-space/VirtualSpaceUtil";
import SpacesSidebar from "../../features/virtual-space/components/Sidebar/SpacesSidebar";
import LoadingView from "../../features/virtual-space/components/SpaceView/LoadingView/LoadingView";
import SpacePlayerView from "../../features/virtual-space/components/SpaceView/SpacePlayer/SpacePlayerView";
import useSpacePlayerController from "../../features/virtual-space/useSpacePlayerController";
import { BreathingWidgetProvider } from "../../features/widget-workspace/components/Widget/BreathingWidget/BreathingWidgetContext";
import Workspace from "../../features/widget-workspace/components/Workspace";
import { AppModeContext } from "./AppModeContext";
import "./LifeAtApp.scss";
import OverlayButtons from "./OverlayButtons/OverlayButtons";
import TopNavigation from "./TopNavigation/TopNavigation";

const UNSUPPORTED_BROWSER_ALERT_MSG =
    "It looks like you're on an unsupported browser.\n\nFor an optimal LifeAt experience, please switch to Chrome, Safari, Firefox, or Edge to access LifeAt.";

interface LifeAtAppParams {
    readonly roomId?: string;
    /** @deprecated space id should not be passed as route param anymore, use query param */
    readonly spaceId?: string;
}
/**
 * This is the main entry point for the LifeAt Spaces application.
 * @constructor
 */
export default function LifeAtApp() {
    const history = useHistory();

    const { spaceId, roomId }: LifeAtAppParams = useParams();

    const { user, userData }: UserContextType = useUserContext();

    const [renderLifeAt, setRenderLifeAt] = useState(true);

    useTaskMigration();
    useProjectsData(user?.uid);

    const appModeQueryParam = RouteUtil.getQueryParamValue(QueryParameters.AppMode) as AppMode;
    const [appMode, setAppMode] = useState<AppMode>(appModeQueryParam ?? AppMode.Focus);
    useEffect(() => {
        if (appModeQueryParam) {
            setAppMode(appModeQueryParam);
        }
    }, [appModeQueryParam]);

    const sidebarCalendarQueryParam = RouteUtil.getQueryParamValue(QueryParameters.Calendar);
    // App loads with sidebar closed on mobile phones.
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(!isMobile || isTablet);
    const [sidebarContent, setSidebarContent] = useState<FocusModeSidebarContentType>(
        sidebarCalendarQueryParam ? "calendar" : "spaces",
    );

    // Set state to last viewed content when data loads.
    useOnUserDataLoaded((loadedData: UserEntity) => {
        if (roomId && isRoomOwnedByUser(roomId, loadedData)) {
            if (loadedData?.lastViewedContent?.appMode && !appModeQueryParam) {
                const lastAppMode = loadedData.lastViewedContent.appMode;
                // NOTE: Explore Mode is not a valid app mode to load into.
                if (lastAppMode === "explore") {
                    setAppMode("focus");
                } else {
                    setAppMode(lastAppMode ?? "focus");
                }
            }

            if (loadedData?.lastViewedContent?.focusSidebar && !sidebarCalendarQueryParam) {
                setSidebarContent(loadedData.lastViewedContent.focusSidebar ?? "spaces");
            }
        }
    });

    useEffect(() => {
        if (!(isChrome || isSafari || isChromium || isEdgeChromium || isFirefox || isElectron)) {
            alert(UNSUPPORTED_BROWSER_ALERT_MSG);
        }
    }, []);

    useEffect(() => {
        // Note: Space information as a route (e.g. "/space/1234") is deprecated.
        // But some things (e.g. explore page) still link to LifeAt using the routes for
        // space IDs, so we support it by using query parameters to replace it if detected.
        if (spaceId) {
            history.replace({
                pathname: `/${Routes.App}`,
                search: RouteUtil.updateQueryParams(QueryParameters.Space, spaceId),
            });
        }
    }, []);

    useOnUserDataLoaded(() => {
        if (user && userData) {
            if (user && user.emailVerified && !userData.displayName) {
                initUserDisplayNameInDbIfMissing(user);
            }
        }
    });

    // If a link to a specific space was used to access our app, we want to use
    // it to load that space for the user immediately.
    const spaceController = useSpacePlayerController(
        spaceId || RouteUtil.getQueryParamValue(QueryParameters.Space) || undefined,
    );
    useRoomSpaceSync(spaceController);
    const { masterVolume } = useGlobalAudioContext();

    useEffect(() => {
        if (window.todesktop) {
            const closeListener = () => setRenderLifeAt(false);
            const unsubscribe = addOnCloseMainWindowEventListener(closeListener);

            return () => unsubscribe?.();
        }
        return undefined;
    }, []);

    useEffect(() => {
        if (window.todesktop) {
            const showListener = () => setRenderLifeAt(true);
            const unsubscribe = addOnShowMainWindowEventListener(showListener);

            return () => unsubscribe?.();
        }
        return undefined;
    }, []);

    const handleSidebarWidgetToggle = (contentType: FocusModeSidebarContentType) => {
        if (sidebarContent !== contentType) {
            setSidebarContent(contentType);
            if (user) {
                updateLastViewedFocusSidebarContent(user.uid, contentType);
            }

            // If the sidebar is open already, switching the contents should not trigger
            // the sidebar to close. So we only trigger it to open when closed.
            if (!isSidebarOpen) {
                setIsSidebarOpen(true);
            }
        } else {
            // If the content is the same as currently selected, toggle visibility of the sidebar.
            setIsSidebarOpen(!isSidebarOpen);
        }
    };

    useEffect(() => {
        // If the current space is removed, show a browser alert and redirect the user to /app.
        if (spaceController?.selectedSpace?.removed) {
            alert("This space is not available. Please try a different space.");
            spaceController.playNextSpaceInCategory(SpaceCategory.NATURE);
        }
    }, [spaceController?.selectedSpace]);

    const focusModeAppComponents = (
        <>
            {spaceController.selectedSpace && (
                <VirtualSpaceContext.Provider value={{ spaceController: spaceController }}>
                    <div id={"appFocusMode"}>
                        <TopNavigation toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

                        <OverlayButtons />

                        <SpacesSidebar
                            sidebarContent={sidebarContent}
                            sidebarOpen={isSidebarOpen}
                            toggleSidebarOpen={() => setIsSidebarOpen(!isSidebarOpen)}
                        />

                        <RoomChatSidebar />

                        <span id="spacePlayerMain">
                            <SpacePlayerView
                                videoUrl={spaceController.selectedSpace.videoUrl}
                                volume={spaceController.playerVolume * masterVolume}
                                isBlockedSpace={
                                    !isLegacyUserWithFreeSpaceAccess(user, spaceController?.selectedSpace?.category) &&
                                    !userData?.proUserInfo?.isProUser &&
                                    !!spaceController.selectedSpace.isPremium
                                }
                                category={spaceController.selectedSpace.category}
                                spaceName={spaceController.selectedSpace.name}
                            />
                        </span>
                    </div>
                </VirtualSpaceContext.Provider>
            )}

            <span id="widgetWorkspace">
                <Workspace
                    sidebarContent={sidebarContent}
                    sidebarOpen={isSidebarOpen}
                    toggleSpacesSidebar={() => handleSidebarWidgetToggle("spaces")}
                    toggleCalendarSidebar={() => handleSidebarWidgetToggle("calendar")}
                    toggleSoundboardSidebar={() => handleSidebarWidgetToggle("soundboard")}
                    toggleAppsSidebar={() => handleSidebarWidgetToggle("apps")}
                />
            </span>
        </>
    );

    const userIsPro = getUserSubscriptionTier(userData) === "pro";

    const focusApp = (
        <BreathingWidgetProvider>
            <RoomContextProvider>
                <div id="app">
                    {/* We always render focus mode so that functionality can continue to
                            run in the background if the user switches to Planning Mode */}
                    {focusModeAppComponents}

                    {userIsPro && appMode === AppMode.Planner && <Planner />}
                </div>
            </RoomContextProvider>
        </BreathingWidgetProvider>
    );

    const exploreApp = <ExploreView />;

    const appElement = () => {
        if (user && !user.emailVerified) {
            return <DesktopAppMainLanding />;
        } else if (!user && (isMobile || isTablet)) {
            return <DesktopAppMainLanding />;
        } else if (!renderLifeAt) {
            return <LoadingView isAppLoading={true} />;
        } else {
            switch (appMode) {
                case "focus":
                case "planner":
                    return focusApp;
                case "explore":
                    return exploreApp;
                default:
                    return focusApp;
            }
        }
    };

    return (
        <div>
            <AppModeContext.Provider
                value={{
                    appMode: appMode,
                    setAppMode: (appMode: AppMode) => setAppMode(appMode),
                }}
            >
                <RoomControllerProvider>
                    {window.todesktop && <AppMenuShortcutListeners />}
                    <OnboardingVisibilityController />
                    {createPortal(<link rel="canonical" href="https://lifeat.io/app" />, document.head)}
                    {appElement()}
                </RoomControllerProvider>
            </AppModeContext.Provider>
        </div>
    );
}
